import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, computed, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { InputComponent } from "../input/input.component";
import { SelectedComponent } from "../selected/selected.component";
import { CommonModule } from '@angular/common';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { ASSOCIATED_INPUTS, ASSOCIATED_SELECTORS } from 'app/config/constants.config';
import {
  buildPreloadedConfig,
  buildPreloadedSelectConfig,
  normalizeFieldValue,
  mergeSelectOptionsWithPreload,
  resolveOptions,
  resolveRawFieldValue,
} from '../utils/field-preload.utils';

@Component({
  selector: 'app-associated',
  standalone: true,
  imports: [
    InputComponent,
    ReactiveFormsModule,
    SelectedComponent,
    CommonModule,
    AutocompleteComponent
  ],
  templateUrl: './associated.component.html'
})
export class AssociatedComponent implements OnInit, OnChanges, OnDestroy {

  form: FormGroup;
  dataInputs = ASSOCIATED_INPUTS;
  dataSelected = ASSOCIATED_SELECTORS;

  @Output() formValid = new EventEmitter<any>();
  @Input() defaultData: any;
  @Output() validData = new EventEmitter<any>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;
  @Input() isLightVersion = false;
  @Input() resetTimer = false;

  companies: any[] = [];
  codpros: any[] = [];
  private isUpdating = false;
  private subscription: any;

  lightDataInputs = computed(() => {
    if (!this.isLightVersion) return this.dataInputs();
    return this.dataInputs().filter((input: any) =>
      input.formName === 'income'
    );
  });

  lightDataSelected = computed(() => {
    if (!this.isLightVersion) return this.dataSelected();
    return [];
  });

  constructor(
    private _listService: ListsService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup({
      since: new FormControl(null, [Validators.required]),
      code: new FormControl(null),
      codpro: new FormControl(null, [Validators.required]),
      academic: new FormControl(null, [Validators.required]),
      company: new FormControl(null, [Validators.required]),
      specialProtecionProgram: new FormControl(null, [Validators.required]),
      income: new FormControl(null, [Validators.required]),
      incomeExtraActivity: new FormControl(null, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();
    this._listService.getCompanies().subscribe(res => { this.companies = res; });
    this.updateFormData();

    this.subscription = this.form.valueChanges.subscribe((value) => {
      if (!this.isUpdating) {
        setTimeout(() => {
          this.emitFormData(value);
        }, 50);
      }
    });
  }

  private preloadSnapshot = '';

  private getPreloadKey(data: any): string {
    return JSON.stringify({
      since: data.since,
      code: data.code,
      codpro: data.codpro,
      academic: resolveRawFieldValue(data, 'academic'),
      company: data.company,
      specialProtecionProgram: resolveRawFieldValue(data, 'specialProtecionProgram'),
      income: data.income,
      incomeExtraActivity: data.incomeExtraActivity,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['defaultData']?.currentValue) return;

    const newData = changes['defaultData'].currentValue;
    if (!newData || !this.hasAddressData(newData)) return;

    const key = this.getPreloadKey(newData);
    if (key === this.preloadSnapshot) return;

    const isFirstPopulation = this.preloadSnapshot === '';
    this.preloadSnapshot = key;

    if (isFirstPopulation) {
      this.updateFormData();
    }
  }

  private hasAddressData(data: any): boolean {
    return data?.asotip != null && data?.asotip !== '';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private emitFormData(value: any) {
    this.formValid.emit({
      ...value,
      _isValid: this.form.valid,
      _formName: 'associated'
    });
  }

  private updateFormData(): void {
    this.isUpdating = true;
    const newData = this.defaultData;

    if (newData && Object.keys(newData).length > 0) {
      this.form.patchValue({
        since: normalizeFieldValue(newData.since, 'date') || null,
        code: newData.code ?? null,
        codpro: newData.codpro ?? null,
        academic: resolveRawFieldValue(newData, 'academic') ?? null,
        company: newData.company ?? null,
        specialProtecionProgram: resolveRawFieldValue(newData, 'specialProtecionProgram') ?? null,
        income: normalizeFieldValue(newData.income, 'number') || null,
        incomeExtraActivity: normalizeFieldValue(newData.incomeExtraActivity, 'number') || null,
      }, { emitEvent: false });

      this.dataInputs.update((inputs: any) =>
        inputs.map((input: any) => buildPreloadedConfig(input, newData[input.formName], input.type))
      );

      this.dataSelected.update((selects: any) =>
        selects.map((select: any) =>
          buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData)
        )
      );
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 200);
  }

  private refreshSelectConfigs(): void {
    const newData = this.defaultData;
    if (!newData || Object.keys(newData).length === 0) return;

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => {
        const currentOptions = resolveOptions(select.options);
        const mergedOptions = mergeSelectOptionsWithPreload(currentOptions, newData, select.formName);
        if (mergedOptions.length !== currentOptions.length && select.options?.set) {
          select.options.set(mergedOptions);
        }
        return buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData);
      })
    );
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => { this.codpros = response; },
      error: (error: any) => { console.error('Error cargando profesiones:', error); }
    });
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        const merged = mergeSelectOptionsWithPreload(
          response,
          this.defaultData,
          'specialProtecionProgram'
        );
        this.dataSelected().at(1)?.options.set(merged);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading special programs:', error); }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading academic levels:', error); }
    });
  }

  setCodpro(event: { value: string | number | null } | string | number | null) {
    const codproId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.patchValue({ codpro: codproId }, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  setCompany(event: { value: string | number | null } | string | number | null) {
    const companyId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.get('company')!.setValue(companyId);
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
    const control = this.form.get(event.formName);
    if (control?.value == event.value) return;

    this.isUpdating = true;
    control?.setValue(event.value, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  getFormNameFromSelected(index: number): string {
    const selected = this.dataSelected().at(index);
    return selected ? (selected.formName ?? '') : '';
  }

  getOptionLabel(index: number, value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const selectConfig = this.dataSelected().at(index);
    if (selectConfig && selectConfig.options) {
      const option = selectConfig.options().find((opt: any) => opt.value == value);
      return option ? option.label : value.toString();
    }
    return value.toString();
  }

  getCompanyLabel(value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const company = this.companies.find(c => c.value == value);
    return company ? company.label : value.toString();
  }

  getProfessionLabel(value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const profession = this.codpros.find(p => p.value == value);
    return profession ? profession.label : value.toString();
  }
}
